import { coastguardInstance } from "../../security";
import { showSnackbar } from "@cofano/cck";

/**
 * A fetch wrapper function which will handle default errors. Accepts Output and Input types as generics
 *
 * @param {Object} obj - Options
 * @param {string} obj.url - The url path of the request (example: `/users`)
 * @param {("GET"|"POST"|"PUT"|"PATCH"|"DELETE")} [obj.method] - The HTTP method type (see {@link fetch} docs for options)
 * @param {Object} [obj.body] - The optional body of the request
 * @param {boolean} [obj.defaultErrorHandling] - Whether it should automatically display the error message
 */
export const request = <O, I = unknown,>({
	url,
	method = "GET",
	body,
	defaultErrorHandling = true,
	contentType = "application/json",
}: {
	url: string,
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
	body?: I,
	defaultErrorHandling?: boolean,
	contentType?: string,
}): Promise<O> => {
	return coastguardInstance.fetch(`/${url}`, {
		method: method,
		body: body === null ? body as BodyInit : JSON.stringify(body),
		credentials: "include",
		mode: "cors",
		headers: {
			"Content-Type": contentType,
			"Cache-Control": "no-cache",
			"Pragma": "no-cache",
		},
	}).then(response => {
		// If response is ok (200 - 299)
		if (response.ok) {
			// If response has no content/content-type
			return response.text().then(textResponse => {
				if (textResponse === "") {
					Promise.resolve();
				} else {
					return JSON.parse(textResponse) as O;
				}
			});
			// If response is not ok (eg 400, 404, 500) and response is json (BOS Exception)
		} else {
			return response.json().then(exception => {
				if (defaultErrorHandling) {
					showSnackbar(<ExceptionErrorContent response={exception} />, { variant: "error" });
				}
				return Promise.reject(exception as Error);
			});
		}
	}).catch((response?: { error: { message?: string } }) => {
		if (response?.error.message) {
			showSnackbar(response.error.message, { variant: "error" });
		}
		return Promise.reject(response);
	}) as Promise<O>;
};

export const ExceptionErrorContent = ({ response }: {
	response: {
		error: {
			code: string;
			message: string;
		},
		parameters?: {
			fieldName: string;
			message: string;
		}[];
	}
}) => (
	<div style={{ maxWidth: 350 }}>
		<p>{response.error.code}</p>

		<span style={{ wordWrap: "break-word" }}>{response.error.message}</span>

		{response.parameters && (
			<ul>
				{response.parameters.map(param => {
					return (
						<li key={param.fieldName}>
							<b>{param.fieldName}</b> - {param.message}
						</li>
					);
				})}
			</ul>
		)}
	</div>
);
